// BulkIgnoreModal.js — Bulk ignore alarms confirmation modal (PRD-49)

import React, { useState } from 'react';
import { bulkDisposition } from '../services/driftService';

function BulkIgnoreModal({ isOpen, onClose, alarmIds, onSuccess }) {
    const [reason, setReason] = useState('');
    const [scope, setScope] = useState('host');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Default expiration: 5 years from now
    const defaultExpiry = new Date(Date.now() + 5 * 365 * 86400000).toISOString().split('T')[0];
    const [expiresAt, setExpiresAt] = useState(defaultExpiry);

    if (!isOpen || !alarmIds || alarmIds.length === 0) return null;

    const count = alarmIds.length;

    const handleSubmit = async () => {
        if (reason.trim().length < 10) {
            setError('Reason must be at least 10 characters.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await bulkDisposition(alarmIds, 'ignore', {
                expiresAt: new Date(expiresAt).toISOString(),
                scope,
                reason: reason.trim(),
            });
            onSuccess && onSuccess(result);
            onClose();
        } catch (err) {
            setError(err.message || 'Bulk ignore failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="alarm-modal-backdrop" onClick={onClose}>
            <div className="alarm-modal" onClick={e => e.stopPropagation()}>
                <div className="alarm-modal__header">
                    <h3>Ignore {count} Alarm{count !== 1 ? 's' : ''}</h3>
                    <button className="alarm-modal__close" onClick={onClose}>×</button>
                </div>
                <div className="alarm-modal__body">
                    <p className="alarm-modal__desc">
                        Ignoring <strong>{count} alarm{count !== 1 ? 's' : ''}</strong> marks them as false positives.
                        Findings will be <strong>removed from your risk score</strong> and hidden from alarms until the expiration date.
                        Use this only when you are certain the detections are wrong.
                    </p>

                    <div className="alarm-modal__field">
                        <label>Expires on</label>
                        <input
                            type="date"
                            className="alarm-modal__input"
                            value={expiresAt}
                            onChange={e => setExpiresAt(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                        />
                    </div>

                    <div className="alarm-modal__field">
                        <label>Scope</label>
                        <div className="alarm-modal__pills">
                            <button
                                className={`alarm-modal__pill ${scope === 'host' ? 'active' : ''}`}
                                onClick={() => setScope('host')}
                                type="button"
                            >
                                This host only
                            </button>
                            <button
                                className={`alarm-modal__pill ${scope === 'asset' ? 'active' : ''}`}
                                onClick={() => setScope('asset')}
                                type="button"
                            >
                                Entire asset
                            </button>
                        </div>
                    </div>

                    <div className="alarm-modal__field">
                        <label>Reason <span className="alarm-modal__hint">(min 10 chars)</span></label>
                        <textarea
                            className="alarm-modal__textarea"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g., These ports are behind a reverse proxy and not directly accessible"
                            rows={3}
                        />
                    </div>

                    {error && <div className="alarm-modal__error">{error}</div>}
                </div>
                <div className="alarm-modal__footer">
                    <button className="alarm-modal__btn alarm-modal__btn--cancel" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className="alarm-modal__btn alarm-modal__btn--ignore"
                        onClick={handleSubmit}
                        disabled={loading || reason.trim().length < 10}
                    >
                        {loading ? `Ignoring ${count}...` : `Ignore ${count} Alarm${count !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BulkIgnoreModal;
